import {
  type EditionAendern,
  FUNKTIONSNAMEN,
  type Funktion,
  funktionen,
  monatspreisCent,
  type VerwaltungEdition,
} from '@nac2s/shared'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import { Input } from '@/components/base/input/input'
import { Dialogfenster } from '@/teile/Dialogfenster'
import { Knopf } from '@/teile/Knopf'
import { Status } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { ApiError } from '../api/client.js'
import {
  editionAendern,
  editionAnlegen,
  editionLoeschen,
  editionPreisAnlegen,
  ladeEditionen,
} from '../api/verwaltung.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { aktuellerMonat, datum, euro, istMonat, monatsname } from '../teile/monat.js'
import { cent } from './PartnerDetail.js'

/**
 * Editionen (ADR-0014): Funktionen zusammenklicken, Namen pflegen, ein Ende
 * setzen. Der Schluessel bleibt — er steht in Tagesstaenden und Preislisten.
 * Was ein Partner davon verkaufen darf, steht in seinem Katalog
 * (Partnerdetail → Katalog).
 */

type Ladezustand =
  | { status: 'laedt' }
  | { status: 'da'; editionen: VerwaltungEdition[] }
  | { status: 'fehler'; meldung: string }

export function Editionen() {
  const [zustand, setZustand] = useState<Ladezustand>({ status: 'laedt' })
  const [neuOffen, setNeuOffen] = useState(false)
  const [bearbeiten, setBearbeiten] = useState<VerwaltungEdition | null>(null)

  const laden = useCallback(async () => {
    try {
      setZustand({ status: 'da', editionen: await ladeEditionen() })
    } catch (fehler: unknown) {
      setZustand({
        status: 'fehler',
        meldung: fehler instanceof ApiError ? fehler.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  return (
    <div>
      <Seitenkopf
        beisatz="Alle Partner"
        titel="Editionen"
        satz="Was eine Edition kann, legt netable hier fest. Was ein Partner verkaufen darf, steht in seinem Katalog."
        rechts={
          <Knopf stark onClick={() => setNeuOffen(true)}>
            Edition anlegen
          </Knopf>
        }
      />
      <div className="px-6 pb-8">
        {zustand.status === 'laedt' ? (
          <Laedt>Editionen werden geladen …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : zustand.editionen.length === 0 ? (
          <Leer titel="Noch keine Edition">Die erste Edition legt fest, was verkauft wird.</Leer>
        ) : (
          <Tabelle
            kopf={['Edition', 'Funktionen', 'VK', 'Laufzeit', 'Partner', 'Kunden', 'Ende', '']}
          >
            {zustand.editionen.map((e) => (
              <tr key={e.key} className="border-b border-secondary last:border-b-0">
                <td className="py-2.5 pr-2.5">
                  <span className="block font-medium text-primary">{e.name}</span>
                  <span className="block text-[11.5px] text-quaternary">{e.key}</span>
                </td>
                <td className="py-2.5 pr-2.5">
                  <span className="flex flex-wrap gap-1">
                    {funktionen.map((f) => (
                      <Status key={f} ton={e.funktionen.includes(f) ? 'gut' : 'ruhig'}>
                        {FUNKTIONSNAMEN[f].name}
                      </Status>
                    ))}
                  </span>
                </td>
                <td className="py-2.5 pr-2.5 tabular-nums">
                  {e.vkCent === null ? (
                    <Status ton="wartet">kein VK</Status>
                  ) : (
                    <span className="text-primary">{euro(e.vkCent)}</span>
                  )}
                </td>
                <td className="py-2.5 pr-2.5 text-[12.5px] text-tertiary tabular-nums">
                  {e.mindestlaufzeitMonate} Monate
                  <span className="block text-[11.5px] text-quaternary">
                    jährlich −{e.monatlichAufschlagProzent} % · monatlich{' '}
                    {e.vkCent === null
                      ? '—'
                      : euro(monatspreisCent(e.vkCent, e.monatlichAufschlagProzent))}
                  </span>
                </td>
                <td className="py-2.5 pr-2.5 text-tertiary tabular-nums">{e.partner}</td>
                <td className="py-2.5 pr-2.5 text-tertiary tabular-nums">{e.kunden}</td>
                <td className="py-2.5 pr-2.5">
                  {e.eolAm ? <Status ton="wartet">endet {datum(e.eolAm)}</Status> : null}
                </td>
                <td className="py-2.5 text-right">
                  <Knopf onClick={() => setBearbeiten(e)}>Bearbeiten</Knopf>
                </td>
              </tr>
            ))}
          </Tabelle>
        )}
      </div>

      {neuOffen ? (
        <EditionFormular
          onAbbruch={() => setNeuOffen(false)}
          onFertig={() => {
            setNeuOffen(false)
            void laden()
          }}
        />
      ) : null}
      {bearbeiten ? (
        <EditionFormular
          edition={bearbeiten}
          onAbbruch={() => setBearbeiten(null)}
          onFertig={() => {
            setBearbeiten(null)
            void laden()
          }}
        />
      ) : null}
    </div>
  )
}

/** Anlegen oder bearbeiten — dieselbe Maske; der Schluessel ist nur beim Anlegen frei. */
function EditionFormular({
  edition,
  onAbbruch,
  onFertig,
}: {
  edition?: VerwaltungEdition
  onAbbruch: () => void
  onFertig: () => void
}) {
  const [key, setKey] = useState(edition?.key ?? '')
  const [name, setName] = useState(edition?.name ?? '')
  const [gewaehlt, setGewaehlt] = useState<Set<Funktion>>(new Set(edition?.funktionen ?? []))
  const [eolAm, setEolAm] = useState(edition?.eolAm ?? '')
  // Nicht vorbelegen: Laufzeit und Jahresrabatt gehoeren zur Edition und
  // werden beim Anlegen bewusst gesetzt — eine Zahl aus dem Code waere geraten.
  const [laufzeit, setLaufzeit] = useState(edition ? String(edition.mindestlaufzeitMonate) : '')
  const [aufschlag, setAufschlag] = useState(
    edition ? String(edition.monatlichAufschlagProzent) : '',
  )
  const [vk, setVk] = useState('')
  const [vkAb, setVkAb] = useState(aktuellerMonat())
  const [vkLaeuft, setVkLaeuft] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const keyGueltig = /^[a-z0-9][a-z0-9_-]{1,39}$/.test(key)
  const eolGueltig = eolAm === '' || /^\d{4}-\d{2}-\d{2}$/.test(eolAm)
  const laufzeitZahl = /^\d{1,3}$/.test(laufzeit.trim()) ? Number(laufzeit) : Number.NaN
  const aufschlagZahl = /^\d{1,2}$/.test(aufschlag.trim()) ? Number(aufschlag) : Number.NaN
  const zahlenGueltig =
    Number.isInteger(laufzeitZahl) &&
    laufzeitZahl >= 1 &&
    laufzeitZahl <= 120 &&
    Number.isInteger(aufschlagZahl) &&
    aufschlagZahl >= 0 &&
    aufschlagZahl <= 99
  const vkCent = cent(vk)

  function umschalten(f: Funktion, an: boolean) {
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (an) neu.add(f)
      else neu.delete(f)
      return neu
    })
  }

  async function absenden(ereignis: FormEvent<HTMLFormElement>) {
    ereignis.preventDefault()
    setLaeuft(true)
    setMeldung(null)
    try {
      if (edition) {
        const aenderung: EditionAendern = {
          name: name.trim(),
          funktionen: [...gewaehlt],
          eolAm: eolAm === '' ? null : eolAm,
          mindestlaufzeitMonate: laufzeitZahl,
          monatlichAufschlagProzent: aufschlagZahl,
        }
        await editionAendern(edition.key, aenderung)
      } else {
        await editionAnlegen({
          key,
          name: name.trim(),
          funktionen: [...gewaehlt],
          mindestlaufzeitMonate: laufzeitZahl,
          monatlichAufschlagProzent: aufschlagZahl,
          ...(vkCent !== null ? { vkCent } : {}),
        })
      }
      onFertig()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.code === 'schluessel_belegt'
          ? 'Diesen Schlüssel gibt es schon.'
          : fehler instanceof ApiError
            ? fehler.message
            : 'Nicht gespeichert.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  async function vkAnlegen() {
    if (!edition || vkCent === null || !istMonat(vkAb)) return
    setVkLaeuft(true)
    setMeldung(null)
    try {
      await editionPreisAnlegen(edition.key, { gueltigAb: vkAb, vkCent })
      onFertig()
    } catch (fehler: unknown) {
      setMeldung(fehler instanceof ApiError ? fehler.message : 'VK nicht angelegt.')
    } finally {
      setVkLaeuft(false)
    }
  }

  async function loeschen() {
    if (!edition) return
    setLaeuft(true)
    setMeldung(null)
    try {
      await editionLoeschen(edition.key)
      onFertig()
    } catch (fehler: unknown) {
      setMeldung(
        fehler instanceof ApiError && fehler.code === 'in_gebrauch'
          ? 'Die Edition steht noch in einem Katalog oder in Mengen. Ein Ende setzen statt löschen.'
          : fehler instanceof ApiError
            ? fehler.message
            : 'Nicht gelöscht.',
      )
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Dialogfenster
      titel={edition ? `Edition ${edition.name}` : 'Edition anlegen'}
      satz="Der Schlüssel bleibt für immer — er steht in Tagesständen und Preisen. Der Name darf sich ändern."
      breite="w-[min(34rem,calc(100vw-2rem))]"
      onSchliessen={onAbbruch}
    >
      <form onSubmit={absenden} className="flex flex-col gap-4 pb-6">
        <Input
          isRequired
          label="Schlüssel"
          hint="Klein, ohne Leerzeichen: a–z, 0–9, - und _."
          size="md"
          value={key}
          onChange={(w) => setKey(w.toLowerCase())}
          isDisabled={edition !== undefined}
          placeholder="z. B. premium"
        />
        <Input isRequired label="Name" size="md" value={name} onChange={setName} />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-secondary">Funktionen</legend>
          {funktionen.map((f) => (
            <Checkbox
              key={f}
              size="md"
              label={FUNKTIONSNAMEN[f].name}
              hint={FUNKTIONSNAMEN[f].beschreibung}
              isSelected={gewaehlt.has(f)}
              onChange={(an) => umschalten(f, an)}
            />
          ))}
        </fieldset>
        <div className="grid grid-cols-2 gap-3">
          <Input
            isRequired
            label="Mindestlaufzeit (Monate)"
            hint="Vorgabe für neue Lizenzen; je Partner änderbar."
            size="md"
            inputMode="numeric"
            value={laufzeit}
            onChange={setLaufzeit}
          />
          <Input
            isRequired
            label="Jahresrabatt (%)"
            hint="Der VK gilt jährlich. Monatlich kündbar kostet VK ÷ (1 − Rabatt) — so stimmt die Angabe jährlich −N % exakt."
            size="md"
            inputMode="numeric"
            value={aufschlag}
            onChange={setAufschlag}
          />
        </div>
        {!edition ? (
          <Input
            label="VK je Seed und Monat (Euro, netto)"
            hint="Gilt ab dem laufenden Monat für alle Partner, sofern keine Abweichung steht. Leer = noch kein Preis."
            size="md"
            inputMode="decimal"
            value={vk}
            onChange={setVk}
            placeholder="0,00"
          />
        ) : null}
        {edition ? (
          <>
            <Input
              label="Ende (EoL)"
              hint="Ab diesem Tag verkauft sie kein Partner mehr; Bestandskunden behalten sie. Die Partner sehen den Hinweis in ihrer Kundenverwaltung. Leer = kein Ende."
              size="md"
              value={eolAm}
              onChange={setEolAm}
              placeholder="JJJJ-MM-TT"
            />
            <fieldset className="flex flex-col gap-2 rounded-xl border border-secondary p-3">
              <legend className="px-1 text-sm font-medium text-secondary">
                VK je Seed und Monat
              </legend>
              {edition.preise.length === 0 ? (
                <p className="text-[12.5px] text-quaternary">
                  Noch kein VK — die Edition hat keinen Preis.
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5 text-[12.5px] text-tertiary tabular-nums">
                  {edition.preise.map((p) => (
                    <li key={`${p.gueltigAb}-${p.angelegtAm}`}>
                      ab {monatsname(p.gueltigAb)}:{' '}
                      <span className="text-primary">{euro(p.vkCent)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                <Input
                  label="Neuer VK (Euro)"
                  size="sm"
                  inputMode="decimal"
                  value={vk}
                  onChange={setVk}
                  placeholder="0,00"
                />
                <Input
                  label="Gültig ab"
                  size="sm"
                  value={vkAb}
                  onChange={setVkAb}
                  placeholder="JJJJ-MM"
                />
                <Knopf
                  disabled={vkLaeuft || vkCent === null || !istMonat(vkAb)}
                  onClick={() => void vkAnlegen()}
                >
                  Preis anpassen
                </Knopf>
              </div>
            </fieldset>
          </>
        ) : null}
        {meldung ? <Fehler>{meldung}</Fehler> : null}
        <div className="flex flex-wrap justify-end gap-2">
          {edition ? (
            <Knopf gefahr onClick={() => void loeschen()} disabled={laeuft} className="mr-auto">
              Löschen
            </Knopf>
          ) : null}
          <Knopf onClick={onAbbruch} disabled={laeuft}>
            Abbrechen
          </Knopf>
          <Knopf
            type="submit"
            stark
            disabled={
              laeuft || !name.trim() || (!edition && !keyGueltig) || !eolGueltig || !zahlenGueltig
            }
          >
            {edition ? 'Speichern' : 'Anlegen'}
          </Knopf>
        </div>
      </form>
    </Dialogfenster>
  )
}
